/**
 * Visor de imágenes del paciente.
 *
 * QUÉ ES Y QUÉ NO ES
 * ==================
 * Es el MISMO visor que usa el radiólogo: el componente `DicomViewer`, con su
 * motor, su calidad de imagen, sus presets de ventana y su panel de series.
 * No hay una segunda implementación que mantener.
 *
 * Lo que cambia no es el visor, es quién mira. Medir y anotar requiere
 * `annotation:create`, que pertenece al radiólogo; el paciente no lo tiene,
 * igual que no lo tiene un tecnólogo. Esa regla la aplica el propio visor a
 * partir de `puedeAnotar`, así que aquí no se decide nada especial: se declara
 * que el paciente no anota, como haría cualquier otra pantalla.
 *
 * Esta página es solo la cáscara: cabecera, volver y el visor. No trae panel
 * de informe, dictado, worklist ni plantillas, porque eso es el puesto de
 * trabajo del radiólogo y no tiene nada que hacer en el portal.
 */

import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, AlertCircle, Loader2, Info, ImageOff } from 'lucide-react'

import {
  getMyStudyById,
  getMyStudyInstances,
} from '@/application/patient-portal'
import { DicomViewer } from '@/presentation/components/dicom'
import { ThumbnailPanel } from '@/presentation/components/dicom/ThumbnailPanel'
import { Button } from '@/presentation/components/ui/Button'
import { urlsParaVisor } from '@/lib/media-url'
import { APP_ROUTES } from '@/config/constants'
import { aFechaSegura } from '@/lib/utils'

/** Fecha en palabras, igual que en el resto del portal. */
const fechaEnPalabras = (fecha: string | null | undefined): string => {
  if (!fecha) return ''
  const d = aFechaSegura(fecha)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })
}

export function PatientViewerPage() {
  const { id } = useParams<{ id: string }>()
  const [imagenActual, setImagenActual] = useState(0)

  const {
    data: study,
    isLoading: cargandoEstudio,
    error: errorEstudio,
  } = useQuery({
    queryKey: ['patient-study', id],
    queryFn: () => getMyStudyById(id!),
    enabled: !!id,
  })

  const { data: imagenes, isLoading: cargandoImagenes } = useQuery({
    queryKey: ['patient-study-instances', id],
    queryFn: () => getMyStudyInstances(id!),
    enabled: !!id,
  })

  const instancias = imagenes?.instances ?? []

  // El visor lee el DICOM original, no el JPG de la galería: es lo que
  // conserva los valores con los que se ajustan brillo y contraste.
  const urls = urlsParaVisor(
    instancias.map((i) => i.fileUrl),
    'paciente'
  )

  if (cargandoEstudio) {
    return (
      <div className="flex h-screen alto-de-pantalla-fijo items-center justify-center bg-slate-950">
        <div className="flex flex-col items-center gap-4 text-slate-300">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p>Abriendo sus imágenes...</p>
        </div>
      </div>
    )
  }

  if (errorEstudio || !study) {
    return (
      <div className="flex h-screen alto-de-pantalla-fijo items-center justify-center bg-slate-950 px-4">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-red-400" />
          <h1 className="text-lg font-medium text-white">No se pudo abrir el examen</h1>
          <p className="mt-2 text-slate-400">
            Vuelva a sus exámenes e inténtelo de nuevo.
          </p>
          <Link to={APP_ROUTES.PATIENT_PORTAL.HOME}>
            <Button variant="outline" className="mt-6">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver a mis exámenes
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  const titulo = study.studyDescription || study.modality || 'Su examen'

  return (
    <div className="flex h-screen alto-de-pantalla-fijo flex-col bg-slate-950">
      {/* Cabecera */}
      <header className="flex flex-shrink-0 items-center justify-between gap-4 border-b border-slate-800 px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <Link to={APP_ROUTES.PATIENT_PORTAL.STUDY_DETAIL(study.id)}>
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver
            </Button>
          </Link>
          <div className="min-w-0">
            <p className="truncate font-medium text-white">{titulo}</p>
            <p className="text-sm text-slate-400">{fechaEnPalabras(study.studyDate)}</p>
          </div>
        </div>

        {/* El diagnóstico es el informe firmado, no lo que se vea aquí. Decirlo
            donde el paciente está mirando, no en un pie de página. */}
        <p className="hidden items-center gap-2 text-sm text-slate-400 sm:flex">
          <Info className="h-4 w-4 flex-shrink-0" />
          Estas imágenes son para su información. El diagnóstico es el informe de su médico.
        </p>
      </header>

      {/* Visor */}
      {cargandoImagenes ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="flex flex-col items-center gap-4 text-slate-300">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p>Cargando las imágenes...</p>
          </div>
        </div>
      ) : instancias.length === 0 ? (
        <div className="flex flex-1 items-center justify-center px-4">
          <div className="max-w-md text-center">
            <ImageOff className="mx-auto mb-4 h-12 w-12 text-slate-600" />
            <h2 className="text-lg font-medium text-white">Este examen no tiene imágenes</h2>
            <p className="mt-2 text-slate-400">
              Algunos exámenes solo tienen informe escrito. Puede leerlo en la
              página del examen.
            </p>
            <Link to={APP_ROUTES.PATIENT_PORTAL.STUDY_DETAIL(study.id)}>
              <Button variant="outline" className="mt-6">
                Ver el examen
              </Button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1">
          {instancias.length > 1 && (
            <ThumbnailPanel
              instances={instancias}
              series={imagenes?.series ?? []}
              currentIndex={imagenActual}
              onSelect={setImagenActual}
              portal="paciente"
            />
          )}

          <div className="relative flex-1">
            <DicomViewer
              studyId={study.id}
              imageUrls={urls}
              showToolbar
              // El paciente no tiene `annotation:create`. Se declara igual que
              // en cualquier otra pantalla; el visor aplica la regla.
              puedeAnotar={false}
              currentImageIndex={instancias.length > 1 ? imagenActual : undefined}
              onImageIndexChange={instancias.length > 1 ? setImagenActual : undefined}
            />
          </div>
        </div>
      )}

      {/* En pantalla estrecha el aviso no cabe arriba, y tiene que verse igual. */}
      <p className="flex items-center justify-center gap-2 border-t border-slate-800 px-4 py-2 text-center text-xs text-slate-400 sm:hidden">
        <Info className="h-3 w-3 flex-shrink-0" />
        Imágenes para su información. El diagnóstico es el informe de su médico.
      </p>
    </div>
  )
}
